/**
 * Shared edit session for a list-defaults tier (workspace or board columns).
 *
 * The draft remembers the tier version the user STARTED editing from
 * (`baseVersion`). Save and Reset-to-inherit quote that version, never the
 * one a background refetch brought in — otherwise a concurrent edit would be
 * silently overwritten. A 409 keeps the draft and asks the user to either
 * discard it or explicitly rebase it onto the newest server version.
 */

import { useState } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { LIST_FIELDS, type ListField } from '@friday/shared/listFields'
import type { ListDefaultsPayload, SaveListDefaultsInput } from './listDefaults'

interface Draft {
  fields: ListField[]
  baseVersion: number
}

export interface ListDefaultsConflict {
  message: string
  /** Readable summary of the newest saved server columns. */
  latest: string
  /** False until the refetch has brought in a newer version. */
  canKeep: boolean
  onDiscard: () => void
  onKeep: () => void
}

const LABELS = new Map<string, string>(LIST_FIELDS)

export function useListDefaultsDraft(
  data: ListDefaultsPayload | undefined,
  refetch: () => Promise<unknown>,
  save: UseMutationResult<{ version: number }, Error, SaveListDefaultsInput>,
  inheritLabel: string,
) {
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState<{ message: string; staleVersion: number } | null>(null)

  function onChange(fields: ListField[]) {
    // First change from clean captures the version the user is looking at.
    setDraft((d) => (d ? { ...d, fields } : { fields, baseVersion: data?.version ?? 0 }))
  }

  async function write(fields: ListField[] | null, fallback: string) {
    if (!data) return
    const version = draft ? draft.baseVersion : data.version
    setError(null)
    setConflict(null)
    try {
      await save.mutateAsync({ fields, version })
      setDraft(null)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Never retried. The draft (if any) is kept; the newest server row is
        // fetched in the background so the user can compare and decide.
        if (draft) setConflict({ message: e.message, staleVersion: version })
        else setError(e.message)
        void refetch()
      } else {
        setError(e instanceof ApiError ? e.message : fallback)
      }
    }
  }

  function discard() {
    setDraft(null)
    setConflict(null)
    setError(null)
  }

  function keep() {
    if (!data || !draft) return
    // Explicit reconciliation: same fields, now checked against the newest
    // version. The user still has to press Save.
    setDraft({ fields: draft.fields, baseVersion: data.version })
    setConflict(null)
  }

  const latest = data?.fields
    ? `Latest saved: ${data.fields.map((f) => LABELS.get(f) ?? f).join(', ')}`
    : `Latest saved: no own columns (${inheritLabel}).`

  const conflictView: ListDefaultsConflict | null =
    conflict && draft
      ? {
          message: conflict.message,
          latest,
          canKeep: !!data && data.version !== conflict.staleVersion,
          onDiscard: discard,
          onKeep: keep,
        }
      : null

  const staleNote =
    draft && !conflict && data && data.version !== draft.baseVersion
      ? 'These columns were changed by someone else since you started editing. Saving will be checked against your starting version.'
      : null

  return {
    pending: draft?.fields ?? null,
    dirty: draft !== null,
    error,
    conflict: conflictView,
    staleNote,
    onChange,
    save: (fallback: string) => (draft ? write(draft.fields, fallback) : Promise.resolve()),
    reset: (fallback: string) => write(null, fallback),
    cancel: discard,
  }
}
